import { RenderManager } from '../../../core/RenderManager';
import { DefenderShip } from '../entities/DefenderShip';
import { Humanoid } from '../entities/Humanoid';

export class Scanner {
  private screenWidth: number;
  private worldWidth: number;
  private scannerWidth: number = 200;
  private scannerHeight: number = 40;
  private scannerX: number;
  private scannerY: number;
  
  constructor(screenWidth: number, worldWidth: number) {
    this.screenWidth = screenWidth;
    this.worldWidth = worldWidth;
    
    // Position scanner at bottom center of screen
    this.scannerX = (screenWidth - this.scannerWidth) / 2;
    this.scannerY = 540; // Near bottom of screen
  }

  public render(
    ctx: CanvasRenderingContext2D,
    ship: DefenderShip | null,
    enemies: any[],
    humanoids: Humanoid[]
  ): void {
    // Draw scanner background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(this.scannerX, this.scannerY, this.scannerWidth, this.scannerHeight);
    
    // Draw scanner border
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    ctx.strokeRect(this.scannerX, this.scannerY, this.scannerWidth, this.scannerHeight);
    
    // Calculate scale factor (world coordinates to scanner coordinates)
    const scale = this.scannerWidth / this.worldWidth;
    
    // Draw ground line
    ctx.strokeStyle = '#55A049';
    ctx.lineWidth = 1;
    const groundY = this.scannerY + this.scannerHeight - 5;
    ctx.beginPath();
    ctx.moveTo(this.scannerX, groundY);
    ctx.lineTo(this.scannerX + this.scannerWidth, groundY);
    ctx.stroke();
    
    // Draw humanoids
    humanoids.forEach(humanoid => {
      if (!humanoid.active) return;
      
      const x = this.scannerX + (humanoid.position.x * scale);
      const y = groundY - 2;
      
      let color = '#55A049'; // Green for safe humanoids
      if (humanoid.isAbducted()) {
        color = '#FFA500'; // Orange for being abducted
      }
      
      ctx.fillStyle = color;
      ctx.fillRect(x, y, 2, 2);
    });
    
    // Draw enemies
    enemies.forEach(enemy => {
      if (!enemy.active) return;
      
      const x = this.scannerX + (enemy.position.x * scale);
      const y = this.scannerY + (enemy.position.y / 600) * (this.scannerHeight - 10) + 5;
      
      let color = '#883932'; // Red for enemies
      
      // Different colors for different enemy types
      if (enemy.type === 'lander') {
        color = '#883932'; // Red for landers
      } else if (enemy.type === 'mutant') {
        color = '#FFFFFF'; // White for mutants
      } else if (enemy.type === 'baiter') {
        color = '#FFFF00'; // Yellow for baiters
      }
      
      ctx.fillStyle = color;
      ctx.fillRect(x, y, 2, 2);
    });
    
    // Draw player ship
    if (ship && ship.active) {
      const x = this.scannerX + (ship.position.x * scale);
      const y = this.scannerY + (ship.position.y / 600) * (this.scannerHeight - 10) + 5;
      
      ctx.fillStyle = '#62D0FE'; // Light blue for player
      ctx.fillRect(x - 1, y - 1, 3, 3); // Slightly larger
    }
    
    // Draw scanner label
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '10px "Courier New", monospace';
    ctx.fillText('SCANNER', this.scannerX, this.scannerY - 5);
  }

  public worldToScannerX(worldX: number): number {
    const scale = this.scannerWidth / this.worldWidth;
    return this.scannerX + (worldX * scale);
  }

  public scannerToWorldX(scannerX: number): number {
    const scale = this.worldWidth / this.scannerWidth;
    return (scannerX - this.scannerX) * scale;
  }

  public isInScanner(screenX: number, screenY: number): boolean {
    return screenX >= this.scannerX && 
           screenX <= this.scannerX + this.scannerWidth &&
           screenY >= this.scannerY && 
           screenY <= this.scannerY + this.scannerHeight;
  }
}